/**
 * 값 변환 공유 모듈의 언어 중립 인터페이스 (value-spec PLAN.md §2).
 * back `app/shared/domain/value_conversion/`과 동일 spec의 TS 구현.
 */

export type ValueKind =
  | "MONETARY"
  | "NUMERIC"
  | "PERCENT"
  | "DATE"
  | "DURATION"
  | "BOOLEAN"
  | "TEXT";

/**
 * 숫자로 다루는 kind — back `_NUMERIC_KINDS`와 동일. negated 반전·dd 버림처럼
 * "숫자 kind 전체"에 적용되는 규칙은 이 상수를 기준으로 판정한다(04 §4, 05 §2).
 */
export const NUMERIC_KINDS = ["MONETARY", "NUMERIC", "PERCENT"] as const;
export type NumericKind = (typeof NUMERIC_KINDS)[number];

export const isNumericKind = (kind: ValueKind): kind is NumericKind =>
  (NUMERIC_KINDS as readonly ValueKind[]).includes(kind);

export type RejectReason =
  | "unit-suffix"
  | "not-numeric"
  | "bad-percent"
  | "bad-date"
  | "bad-duration"
  | "bad-boolean"
  | "type-mismatch"
  | "integer-required"
  | "negative-not-allowed";

/*
 * 값 3종 VO — 파이프라인의 세 단계(자유 입력 / 표시 정규형 / 저장 canonical)는
 * 문자열로는 구분되지 않는데 서로 바꿔 넣으면 이중 환산·이중 반전이 난다.
 * TS에는 nominal typing이 없어 `{ text: string }`끼리 구조적으로 호환되므로,
 * 브랜드 필드로 그 호환을 끊어 컴파일러가 혼동을 막게 한다.
 */

/** 분할·trim이 끝난 자유 입력. 자유 입력이 정의라 불변식이 없다. */
export interface RawValue {
  readonly brand: "raw";
  readonly text: string;
}

/** IXD에 보여지는 값. kind별로 서식이 달라(콤마·괄호·한국어 기간) 검증하지 않는다. */
export interface IXDViewValue {
  readonly brand: "view";
  readonly text: string;
}

/** IXD에 저장되는 값. canonical 형식이 불변식이며 위반 시 생성이 실패한다. */
export interface IXDStoredValue {
  readonly brand: "stored";
  readonly text: string;
}

export const rawValue = (text: string): RawValue => ({ brand: "raw", text });

export const viewValue = (text: string): IXDViewValue => ({
  brand: "view",
  text,
});

// back `vo._ensure_canonical`과 동일한 규칙 — 두 구현이 같은 저장값을 주고받는다.
const CANONICAL_NUMBER_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d*[1-9])?$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const STORED_DURATION_PATTERN = /^P(?=\d)(?:\d+Y)?(?:\d+M)?(?:\d+D)?$/;

function ensureCanonical(text: string, kind: ValueKind): void {
  // TEXT는 원문 보존이 계약이고, 빈 문자열은 "값 없음"이라 어느 kind에서도 무제약.
  if (kind === "TEXT" || text === "") return;
  if (text !== text.trim()) {
    throw new Error(`저장값에 앞뒤 공백이 있다: ${JSON.stringify(text)}`);
  }

  switch (kind) {
    case "DATE": {
      if (!ISO_DATE_PATTERN.test(text)) {
        throw new Error(
          `저장 날짜는 YYYY-MM-DD여야 한다: ${JSON.stringify(text)}`,
        );
      }
      // Date 생성자는 2023-13-45를 다음 해로 넘겨 받으므로 왕복 비교로 달력을 검증한다.
      const parsed = new Date(`${text}T00:00:00Z`);
      if (
        Number.isNaN(parsed.getTime()) ||
        parsed.toISOString().slice(0, 10) !== text
      ) {
        throw new Error(`달력에 없는 날짜다: ${JSON.stringify(text)}`);
      }
      return;
    }

    case "DURATION":
      if (!STORED_DURATION_PATTERN.test(text)) {
        throw new Error(
          `저장 기간은 PnYnMnD여야 한다: ${JSON.stringify(text)}`,
        );
      }
      return;

    case "BOOLEAN":
      if (text !== "true" && text !== "false") {
        throw new Error(
          `저장 불리언은 true/false여야 한다: ${JSON.stringify(text)}`,
        );
      }
      return;

    case "MONETARY":
    case "NUMERIC":
    case "PERCENT":
      if (!CANONICAL_NUMBER_PATTERN.test(text) || text === "-0") {
        throw new Error(
          `저장 숫자는 콤마·괄호·지수·trailing zero·-0 없는 canonical이어야 한다: ${JSON.stringify(text)}`,
        );
      }
      return;
  }
}

/**
 * 저장값 생성 — canonical 형식을 검증하고 위반 시 throw(fail-loud).
 * 저장값이 형식을 어기면 편집기가 읽는 payload가 깨지므로 조용한 폴백 대신 여기서 막는다.
 */
export const storedValue = (text: string, kind: ValueKind): IXDStoredValue => {
  ensureCanonical(text, kind);
  return { brand: "stored", text };
};

/** 형식이 이미 보장된 경로 전용 — 검증을 건너뛴다. 사용처에 WHY를 남길 것. */
export const trustedStoredValue = (text: string): IXDStoredValue => ({
  brand: "stored",
  text,
});

export interface ValueContext {
  kind: ValueKind;
  /** 원본 dataType/schemaType (예: "nonNegativeIntegerItemType") */
  subKind?: string | null;
  /** 10^N 배율 (front unitDivisor=10^scale). 기본 0 */
  scale?: number;
  /** .frm negated 서식. 기본 false */
  negated?: boolean;
  /** .frm CurrencyDecimalDigits. null이면 제약 없음 */
  decimalDigits?: number | null;
  /** 빌딩 탭 헤더 % 컨텍스트 (back 전용 경로). 기본 false */
  percentHeader?: boolean;
  /** front는 항상 "user" (DSD 관용 경로는 back 전용) */
  source?: "dsd" | "user";
}

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; reason: RejectReason; original: string };

export const ok = <T>(value: T): Result<T> => ({ ok: true, value });

export const reject = (
  reason: RejectReason,
  original: string,
): Result<never> => ({ ok: false, reason, original });
